import { createContext, useContext, useMemo, useState } from 'react'

/**
 * The date the whole application is answering about.
 *
 * Held once, at the top, because it is the product's central idea rather than a
 * filter on one screen. Set it to last January and the roster, the payroll
 * total, the band judgements and the equity report all answer for last January
 * — which is the question a spreadsheet cannot answer at all, and the reason
 * revisions are effective-dated instead of overwritten.
 *
 * `undefined` means today, as decided by the server's clock rather than the
 * browser's. A laptop with a wrong date must not silently change what the
 * organisation appears to pay.
 */
interface AsOfState {
  asOf?: string
  /** The server's today, once anything has reported it. */
  today?: string
  setAsOf: (value?: string) => void
  reportToday: (value: string) => void
  isHistorical: boolean
}

const AsOfContext = createContext<AsOfState | undefined>(undefined)

export function AsOfProvider({ children }: { children: React.ReactNode }) {
  const [asOf, setAsOf] = useState<string>()
  const [today, setToday] = useState<string>()

  const value = useMemo<AsOfState>(
    () => ({
      asOf,
      today,
      setAsOf,
      reportToday: setToday,
      isHistorical: asOf !== undefined && today !== undefined && asOf !== today,
    }),
    [asOf, today],
  )

  return <AsOfContext.Provider value={value}>{children}</AsOfContext.Provider>
}

export function useAsOf(): AsOfState {
  const state = useContext(AsOfContext)
  if (!state) throw new Error('useAsOf must be used inside an AsOfProvider')
  return state
}
